import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  Switch,
  Card,
  Tabs,
  message,
  Space,
  Typography,
  Grid,
} from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import consts from "../../constant";
import Loadingpage from "../Loadingpage/Loadingpage";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const HighLowSetting = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [activeRole, setActiveRole] = useState("basic");
  const screens = useBreakpoint();
  const [hasFetched, setHasFetched] = useState(false);
  const [initialRoleValues, setInitialRoleValues] = useState({});
  const [changedRoles, setChangedRoles] = useState({});

  const normalizeRoleValues = (values = {}) => ({
    admin_commission:
      values.admin_commission === undefined || values.admin_commission === null
        ? values.admin_commission
        : Number(values.admin_commission),
    min_amount:
      values.min_amount === undefined || values.min_amount === null
        ? values.min_amount
        : Number(values.min_amount),
    max_amount:
      values.max_amount === undefined || values.max_amount === null
        ? values.max_amount
        : Number(values.max_amount),
    win_percentage:
      values.win_percentage === undefined || values.win_percentage === null
        ? values.win_percentage
        : Number(values.win_percentage),
    isActive: Boolean(values.isActive),
  });

  const hasRoleChanged = (roleName, currentValues) => {
    return (
      JSON.stringify(normalizeRoleValues(currentValues)) !==
      JSON.stringify(normalizeRoleValues(initialRoleValues[roleName]))
    );
  };

  const fetchSettings = async (role = activeRole) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${consts.BackendUrl}/get-trade-settings`,
        { type: "binary", role },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("Jetix")}`,
          },
        },
      );
      if (data?.success && data?.result) {
        const result = data.result;

        const defaultRoleData = {
          admin_commission: 2,
          min_amount: 0.1,
          max_amount: 100,
          // profit: 90,
          // lucky_round: 10,
          // jackpot_amount: 500,
          // times: [30, 60, 120, 300],
          // amounts: [1, 5, 10, 50],
          isActive: true,
        };

        const roleData = result[role] || {};

        // Deep ensure no missing fields
        const mergedData = {
          admin_commission:
            roleData.admin_commission ??
            result.admin_commission ??
            defaultRoleData.admin_commission,
          min_amount:
            roleData.min_amount ??
            result.min_amount ??
            defaultRoleData.min_amount,
          max_amount:
            roleData.max_amount ??
            result.max_amount ??
            defaultRoleData.max_amount,
          win_percentage:
            roleData.win_percentage ??
            result.win_percentage ??
            0,
          isActive: roleData.isActive ?? true,
          // times: (roleData.times && roleData.times.length > 0) ? roleData.times : defaultRoleData.times,
          // amounts: (roleData.amounts && roleData.amounts.length > 0) ? roleData.amounts : defaultRoleData.amounts
        };

        const normalizedData = normalizeRoleValues(mergedData);

        form.setFieldsValue({
          [role]: normalizedData,
        });
        setInitialRoleValues((prev) => ({
          ...prev,
          [role]: normalizedData,
        }));
        setChangedRoles((prev) => ({
          ...prev,
          [role]: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      messageApi.error("Failed to fetch settings");
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    fetchSettings("basic");
  }, []);

  const handleTabChange = (key) => {
    setActiveRole(key);
    fetchSettings(key);
  };

  const onFinish = async (values, roleName) => {
    setLoading(true);
    try {
      await axios.post(
        `${consts.BackendUrl}/update-trade-settings`,
        {
          type: "binary",
          role: roleName,
          [roleName]: values[roleName],
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("Jetix")}`,
          },
        },
      );
      messageApi.success(
        `${roleName.toUpperCase()} settings updated successfully`,
      );
      fetchSettings(roleName);
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || "Failed to update settings",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderRoleSettingsForm = (roleName) => (
    <Card
      className="card-bg-clr"
      style={{
        marginTop: "20px",
        border: "none",
        background: "rgba(255, 255, 255, 0.03)",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: screens.md ? "1fr 1fr" : "1fr",
          gap: "24px",
        }}
      >
        <Form.Item
          label={<span style={{ color: "#fff" }}>Admin Commission (%)</span>}
          name={[roleName, "admin_commission"]}
          rules={[{ required: true, message: "Please enter admin commission" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="2"
            min={0}
            max={100}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Minimum Amount</span>}
          name={[roleName, "min_amount"]}
          rules={[{ required: true, message: "Please enter minimum amount" }]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="0.10" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Maximum Amount</span>}
          name={[roleName, "max_amount"]}
          rules={[{ required: true, message: "Please enter maximum amount" }]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="20" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Win Percentage (%)</span>}
          name={[roleName, "win_percentage"]}
          rules={[{ required: true, message: "Please enter win percentage" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="90"
            min={0}
            max={100}
          />
        </Form.Item>
        {/* 
        <Form.Item
          label={<span style={{ color: '#fff' }}>Profit (%)</span>}
          name={[roleName, 'profit']}
          rules={[{ required: true, message: 'Please enter profit percentage' }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="90" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: '#fff' }}>Lucky Round</span>}
          name={[roleName, 'lucky_round']}
          rules={[{ required: true, message: 'Please enter lucky round' }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="10" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: '#fff' }}>Jackpot Amount</span>}
          name={[roleName, 'jackpot_amount']}
          rules={[{ required: true, message: 'Please enter jackpot amount' }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="500" min={0} />
        </Form.Item> */}

        {/* <Form.Item
          label={<span style={{ color: "#fff" }}>Is Active</span>}
          name={[roleName, "isActive"]}
          valuePropName="checked"
          initialValue={true}
        >
          <Switch checkedChildren="ON" unCheckedChildren="OFF" />
        </Form.Item> */}
      </div>

      {/* <div style={{ marginTop: '40px', display: 'grid', gridTemplateColumns: screens.lg ? '1fr 1fr' : '1fr', gap: '40px' }}>
        <div>
          <Title level={5} style={{ color: '#fff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Allowed Times (Seconds)
          </Title>
          <Form.List name={[roleName, 'times']}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.08)',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      noStyle
                      rules={[{ required: true, message: '' }]}
                    >
                      <InputNumber
                        placeholder="Sec"
                        bordered={false}
                        className="white-number-input"
                        style={{ width: '60px', fontSize: '14px' }}
                        controls={false}
                      />
                    </Form.Item>
                    <DeleteOutlined
                      onClick={() => remove(name)}
                      style={{ color: '#ff4d4f', cursor: 'pointer', marginLeft: '4px', fontSize: '12px' }}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  style={{
                    background: 'transparent',
                    color: '#FC4420',
                    borderColor: '#FC4420',
                    borderRadius: '8px',
                    height: '38px'
                  }}
                >
                  Add Time
                </Button>
              </div>
            )}
          </Form.List>
        </div>

        <div>
          <Title level={5} style={{ color: '#fff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            Allowed Amounts
          </Title>
          <Form.List name={[roleName, 'amounts']}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.08)',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      noStyle
                      rules={[{ required: true, message: '' }]}
                    >
                      <InputNumber
                        placeholder="Amt"
                        bordered={false}
                        className="white-number-input"
                        style={{ width: '60px', fontSize: '14px' }}
                        controls={false}
                      />
                    </Form.Item>
                    <DeleteOutlined
                      onClick={() => remove(name)}
                      style={{ color: '#ff4d4f', cursor: 'pointer', marginLeft: '4px', fontSize: '12px' }}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  style={{
                    background: 'transparent',
                    color: '#FC4420',
                    borderColor: '#FC4420',
                    borderRadius: '8px',
                    height: '38px'
                  }}
                >
                  Add Amount
                </Button>
              </div>
            )}
          </Form.List>
        </div>
      </div> */}
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={() => {
            form
              .validateFields([roleName])
              .then((values) => {
                onFinish(values, roleName);
              })
              .catch((err) => {
                messageApi.warning("Please fill all required fields");
              });
          }}
          loading={loading}
          disabled={!changedRoles[roleName] || loading}
          style={{
            height: "42px",
            padding: "0 24px",
            borderRadius: "6px",
            background: changedRoles[roleName]
              ? "linear-gradient(90deg, #FC4420 0%, #d9361b 100%)"
              : "rgba(255,255,255,0.16)",
            border: "none",
          }}
        >
          Save {roleName.toUpperCase()} Settings
        </Button>
      </div>
    </Card>
  );

  const tabItems = [
    {
      key: "basic",
      label: <span style={{ padding: "0 12px" }}>Basic Role</span>,
      children: renderRoleSettingsForm("basic"),
    },
    {
      key: "vip",
      label: <span style={{ padding: "0 12px" }}>VIP Role</span>,
      children: renderRoleSettingsForm("vip"),
    },
    {
      key: "vvip",
      label: <span style={{ padding: "0 12px" }}>VVIP Role</span>,
      children: renderRoleSettingsForm("vvip"),
    },
  ];

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#000" }}>
      <Loadingpage isLoading={loading} />
      {contextHolder}

      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          flexDirection: screens.sm ? "row" : "column",
          justifyContent: "space-between",
          alignItems: screens.sm ? "center" : "flex-start",
          gap: "16px",
        }}
      >
        <div>
          <Title
            level={2}
            style={{ color: "#fff", margin: 0, fontSize: "28px" }}
          >
            High-Low Settings
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: "16px" }}>
            Configure trading rules and limits for user roles
          </Text>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changedValues, allValues) => {
          const roleName = Object.keys(changedValues)[0];
          setChangedRoles((prev) => ({
            ...prev,
            [roleName]: hasRoleChanged(roleName, allValues[roleName]),
          }));
        }}
        autoComplete="off"
        requiredMark={false}
      >
        <Tabs
          activeKey={activeRole}
          onChange={handleTabChange}
          items={tabItems}
          className="custom-tabs"
          style={{ color: "#fff" }}
        />
      </Form>
    </div>
  );
};

export default HighLowSetting;
